import React, { useState, useEffect } from 'react';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  Badge,
  FormHelperText
} from '@mui/material';
import {
  Search,
  FilterList,
  Visibility,
  Edit,
  History,
  Business,
  LocationOn,
  Assessment,
  CalendarToday,
  TrendingUp,
  Warning,
  CheckCircle,
  Info,
  AttachFile,
  GetApp,
  Print,
  Email,
  Assignment,
  Cancel,
  Schedule,
  Pending,
  FolderOpen,
  CheckCircleOutline,
  ArrowForward,
  NotificationsActive
} from '@mui/icons-material';
import api from '../../config/axiosConfig';
import JourneyStepper from '../common/JourneyStepper';
import { calculateIva } from '../../utils/quotationTax';

const Historial = () => {
  const [quotations, setQuotations] = useState([]);
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [quotationDetail, setQuotationDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [quotationToReject, setQuotationToReject] = useState(null);
  const [rejectForm, setRejectForm] = useState({ razon: '', sugerencia: '' });
  const [rejectFormError, setRejectFormError] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [statistics, setStatistics] = useState({
    total: 0,
    byStatus: { PENDING: 0, ACCEPTED: 0, REJECTED: 0, EXPIRED: 0 },
    byCompany: {},
    recent: 0
  });
  
  const [filters, setFilters] = useState({
    folio: '',
    cliente: '',
    empresa: '',
    status: '',
    email: '',
    creationMonth: '',
    dateRange: 'all'
  });

  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'

  useEffect(() => {
    fetchQuotations();
    fetchUserInfo();
  }, []);

  const fetchUserInfo = async () => {
    try {
      const response = await api.get('/users/me');
      setUserInfo(response.data);
    } catch (error) {
      console.error('Error al obtener información del usuario:', error);
      // Simulamos datos de usuario para pruebas
      setUserInfo({
        id: 1,
        role: { name: 'ADMIN' }, // Cambiar a 'USER' para probar filtrado por empresa
        company: { id: 1, businessName: 'Empresa Demo 1' }
      });
    }
  };

  // silent: actualización automática -- sin spinner ni borrar la tabla si falla una vuelta
  const fetchQuotations = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);

      const userResponse = await api.get('/users/me');
      const currentUser = userResponse.data;
      setUserInfo(currentUser);
      const role = currentUser?.role?.name || currentUser?.role || '';

      // INVITADO no tiene acceso al catálogo completo de empresas: usa la suya de /users/me
      const [response, companies, servicesResponse] = await Promise.all([
        api.get('/quotations'),
        role === 'INVITADO'
          ? Promise.resolve(currentUser?.company ? [currentUser.company] : [])
          : api.get('/companies').then(r => r.data),
        api.get('/services')
      ]);

      let quotationsData = response.data;
      const services = servicesResponse.data;

      // Enriquecer las cotizaciones con información de empresa y servicio
      quotationsData = quotationsData.map(quotation => {
        const company = companies.find(c => c.id === quotation.companyId);
        const service = services.find(s => s.id === quotation.serviceId);

        // El DTO trae invoiceFolio/name/email/totalAmount; antes se leían campos inexistentes
        // (folio, clientName, clientEmail) y el "subtotal" era el precio de catálogo del servicio
        // de la matriz, no lo cotizado para todos los inmuebles.
        return {
          ...quotation,
          company: company,
          service: service,
          folio: quotation.invoiceFolio,
          clientName: quotation.name,
          clientEmail: quotation.email,
          serviceName: quotation.serviceName || service?.serviceName,
          businessName: company?.businessName || quotation.businessName || 'N/A',
          subtotal: quotation.totalAmount || 0
        };
      });

      // Si el usuario es INVITADO, filtrar solo por su empresa.
      // Importante: si no tiene empresa vinculada, no debe ver nada (antes caía en
      // mostrar TODAS las cotizaciones de TODAS las empresas por error).
      if (role === 'INVITADO') {
        const companyId = currentUser?.company?.id;
        quotationsData = companyId ? quotationsData.filter(q => q.companyId === companyId) : [];
      }
      
      setQuotations(quotationsData);
      calculateStatistics(quotationsData);
      setLoading(false);
      
    } catch (apiError) {
      if (silent) return;
      console.error('Error al obtener cotizaciones:', apiError);
      setError('Error al cargar las cotizaciones. Por favor, intente nuevamente.');
      setQuotations([]);
      setLoading(false);
    }
  };

  useAutoRefresh(() => fetchQuotations({ silent: true }), {
    paused: detailDialogOpen || rejectDialogOpen || !!pdfLoadingId,
  });

  const calculateStatistics = (quotationsData) => {
    const stats = {
      total: quotationsData.length,
      byStatus: { PENDING: 0, ACCEPTED: 0, REJECTED: 0, EXPIRED: 0 },
      byCompany: {},
      recent: 0
    };

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    quotationsData.forEach(quotation => {
      // Count by status
      if (quotation.status) {
        stats.byStatus[quotation.status] = (stats.byStatus[quotation.status] || 0) + 1;
      }

      // Count by company
      if (quotation.businessName) {
        stats.byCompany[quotation.businessName] = 
          (stats.byCompany[quotation.businessName] || 0) + 1;
      }

      // Count recent quotations
      const createdDate = new Date(quotation.createdAt || quotation.created_at);
      if (createdDate > thirtyDaysAgo) {
        stats.recent++;
      }
    });

    setStatistics(stats);
  };

  // Filtrar cotizaciones
  useEffect(() => {
    let filtered = quotations;

    if (filters.folio) {
      filtered = filtered.filter(quotation => 
        quotation.folio?.toLowerCase().includes(filters.folio.toLowerCase())
      );
    }

    if (filters.cliente) {
      filtered = filtered.filter(quotation => 
        quotation.clientName?.toLowerCase().includes(filters.cliente.toLowerCase())
      );
    }

    if (filters.empresa) {
      filtered = filtered.filter(quotation => 
        quotation.businessName?.toLowerCase().includes(filters.empresa.toLowerCase())
      );
    }

    if (filters.status) {
      filtered = filtered.filter(quotation => 
        quotation.status === filters.status
      );
    }

    if (filters.email) {
      filtered = filtered.filter(quotation => 
        quotation.clientEmail?.toLowerCase().includes(filters.email.toLowerCase())
      );
    }

    if (filters.creationMonth) {
      filtered = filtered.filter(quotation => {
        const dateField = quotation.createdAt || quotation.created_at;
        if (!dateField) return false;
        const createdDate = new Date(dateField);
        if (isNaN(createdDate.getTime())) return false;
        const monthYear = `${createdDate.getFullYear()}-${String(createdDate.getMonth() + 1).padStart(2, '0')}`;
        return monthYear === filters.creationMonth;
      });
    }

    if (filters.dateRange !== 'all') {
      const now = new Date();
      let startDate = new Date();
      
      switch (filters.dateRange) {
        case 'week':
          startDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(now.getMonth() - 1);
          break;
        case 'quarter':
          startDate.setMonth(now.getMonth() - 3);
          break;
        case 'year':
          startDate.setFullYear(now.getFullYear() - 1);
          break;
      }

      filtered = filtered.filter(quotation => {
        const createdDate = new Date(quotation.createdAt || quotation.created_at);
        return createdDate >= startDate;
      });
    }

    setFilteredQuotations(filtered);
  }, [filters, quotations]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getAvailableMonths = () => {
    const months = new Set();
    quotations.forEach(quotation => {
      const dateField = quotation.createdAt || quotation.created_at;
      if (dateField) {
        const date = new Date(dateField);
        if (!isNaN(date.getTime())) {
          const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          months.add(monthYear);
        }
      }
    });
    return Array.from(months).sort().reverse();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      DRAFT: { label: 'Borrador', color: 'secondary', icon: <Assignment /> },
      PENDING: { label: 'Pendiente', color: 'warning', icon: <Pending /> },
      ACCEPTED: { label: 'Aceptada', color: 'success', icon: <CheckCircle /> },
      REJECTED: { label: 'Rechazada', color: 'error', icon: <Cancel /> },
      EXPIRED: { label: 'Expirada', color: 'default', icon: <Schedule /> },
      CANCELLED: { label: 'Cancelada', color: 'secondary', icon: <Cancel /> },
      IN_PROGRESS: { label: 'En Proceso', color: 'info', icon: <TrendingUp /> },
      COMPLETED: { label: 'Completada', color: 'primary', icon: <CheckCircle /> },
      INVOICED: { label: 'Facturada', color: 'info', icon: <Assignment /> },
      PAID: { label: 'Pagada', color: 'success', icon: <CheckCircle /> }
    };

    const config = statusConfig[status] || statusConfig.DRAFT;
    
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const handleViewDetails = async (quotation) => {
    setSelectedQuotation(quotation);
    setQuotationDetail(null);
    setDetailDialogOpen(true);
    setDetailLoading(true);
    try {
      const response = await api.get(`/quotations/${quotation.id}`);
      setQuotationDetail(response.data);
    } catch (error) {
      console.error('Error al cargar el detalle de la cotización:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  // Nombre del inmueble de cada concepto: la matriz o la sucursal a la que se cotizó.
  const lineEntityName = (line, entities) => {
    const entity = (entities || []).find(e => (line.entityType === 'MATRIZ'
      ? e.entityType === 'MATRIZ'
      : e.entityType === 'SUCURSAL' && e.branchId === line.entityBranchId));
    return entity?.entityName || (line.entityType === 'MATRIZ' ? 'Matriz' : line.entityType ? 'Sucursal' : 'General');
  };
  const formatMoneyMx = (value) => `$${Number(value || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const openRejectDialog = (quotation) => {
    setQuotationToReject(quotation);
    setRejectForm({ razon: '', sugerencia: '' });
    setRejectFormError('');
    setRejectDialogOpen(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectForm.razon) {
      setRejectFormError('Por favor selecciona un motivo.');
      return;
    }
    if (!quotationToReject) return;

    setRejectSubmitting(true);
    try {
      await api.post(`/quotations/reject/${quotationToReject.id}`, {
        razon: rejectForm.razon,
        sugerencia: rejectForm.sugerencia
      });

      setQuotations(prev => prev.map(q =>
        q.id === quotationToReject.id ? { ...q, status: 'REJECTED' } : q
      ));

      setRejectDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Hemos recibido tu respuesta. Un ejecutivo de CONEM se pondrá en contacto contigo.',
        severity: 'info'
      });
    } catch (error) {
      console.error('Error al rechazar cotización:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'No se pudo enviar tu respuesta. Intenta de nuevo o contáctanos directamente.',
        severity: 'error'
      });
    } finally {
      setRejectSubmitting(false);
    }
  };

  const handleViewPdf = async (quotation) => {
    setPdfLoadingId(quotation.id);
    try {
      const response = await api.get(`/quotations/${quotation.id}/pdf`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      window.open(blobUrl, '_blank');
    } catch (error) {
      console.error('Error al obtener el PDF de la cotización:', error);
      setSnackbar({
        open: true,
        message: 'No se pudo abrir el PDF de la cotización.',
        severity: 'error'
      });
    } finally {
      setPdfLoadingId(null);
    }
  };

  const handleStatusChange = async (quotationId, newStatus) => {
    try {
      // Obtener el estado actual de la cotización
      const currentQuotation = quotations.find(q => q.id === quotationId);
      const currentStatus = currentQuotation?.status;
      
      // Si el estado no ha cambiado, no hacer nada
      if (currentStatus === newStatus) {
        return;
      }
      
      if (newStatus === 'ACCEPTED') {
        // Usar el endpoint específico para aceptar cotización
        // Este endpoint actualiza el estado y genera automáticamente la orden de trabajo
        const response = await api.put(`/quotations/${quotationId}/accept`);
        
        console.log('Cotización aceptada:', response.data);
        
        // Actualizar el estado local
        setQuotations(prev => prev.map(quotation => 
          quotation.id === quotationId 
            ? { ...quotation, status: 'ACCEPTED' }
            : quotation
        ));
        
        setSnackbar({
          open: true,
          message: '✅ Cotización aceptada exitosamente. Se ha generado una orden de trabajo automáticamente. Ahora puedes continuar con el siguiente paso: Llenar Información.',
          severity: 'success'
        });
        
        // Recargar las cotizaciones para obtener datos actualizados
        // Sin hacer window.location.reload() para no perder la sesión
        setTimeout(() => {
          fetchQuotations();
        }, 1000);
        
      } else {
        // Para cualquier otro estado (REJECTED, CANCELLED, etc.), usar el endpoint específico de estado
        // Esto evita que se recalcule el monto total de la cotización
        await api.put(`/quotations/${quotationId}/status`, { status: newStatus });
        
        // Actualizar el estado local
        setQuotations(prev => prev.map(quotation => 
          quotation.id === quotationId 
            ? { ...quotation, status: newStatus }
            : quotation
        ));
        
        // Mensaje específico según el estado
        const statusMessages = {
          'REJECTED': 'Cotización rechazada',
          'CANCELLED': 'Cotización cancelada',
          'EXPIRED': 'Cotización marcada como expirada',
          'COMPLETED': 'Cotización completada',
          'INVOICED': 'Cotización facturada',
          'PAID': 'Cotización marcada como pagada'
        };
        
        setSnackbar({
          open: true,
          message: statusMessages[newStatus] || `Estado actualizado a ${newStatus}`,
          severity: 'info'
        });
      }
      
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.response?.data?.message || 'Error al actualizar el estado de la cotización',
        severity: 'error'
      });
    }
  };

  const StatCard = ({ title, value, icon, color = 'primary' }) => (
    <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 2 }}>
      <CardContent sx={{ textAlign: 'center', p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          {React.cloneElement(icon, { 
            sx: { fontSize: 40, color: `${color}.main` } 
          })}
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: `${color}.main`, mb: 1 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', py: 4 }}>
      <Box sx={{ maxWidth: '1400px', mx: 'auto', px: 3 }}>
        {/* Header Section */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <History sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                Historial de Cotizaciones
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.95, fontWeight: 400 }}>
                {userInfo?.role?.name === 'ADMIN'
                  ? 'Registro Completo del Sistema'
                  : `Cotizaciones de ${userInfo?.company?.businessName || 'su Empresa'}`
                }
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Banner CTA — cotizaciones pendientes de aceptar */}
        {filteredQuotations.filter(q => q.status === 'PENDING' || q.status === 'DRAFT').length > 0 && (
          <Paper elevation={0} sx={{
            mb: 3, p: 3, borderRadius: 3, overflow: 'hidden', position: 'relative',
            background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 60%, #43a047 100%)',
            boxShadow: '0 8px 32px rgba(46,125,50,0.35)',
          }}>
            <Box sx={{ position: 'absolute', right: -20, top: -20, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
            <Box sx={{ position: 'absolute', right: 60, bottom: -40, width: 120, height: 120, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3, flexWrap: 'wrap', position: 'relative' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, p: 1.5, display: 'flex' }}>
                  <NotificationsActive sx={{ color: 'white', fontSize: 32 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 800, lineHeight: 1.2 }}>
                    Tiene {filteredQuotations.filter(q => q.status === 'PENDING' || q.status === 'DRAFT').length} cotización(es) pendiente(s) de aceptar
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.5 }}>
                    Revise los detalles y acepte su cotización para continuar con el proceso de protección civil
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', flexShrink: 0 }}>
                <Button
                  variant="outlined"
                  startIcon={<GetApp />}
                  disabled={pdfLoadingId != null}
                  onClick={() => {
                    const pending = filteredQuotations.find(q => q.status === 'PENDING' || q.status === 'DRAFT');
                    if (pending) handleViewPdf(pending);
                  }}
                  sx={{
                    color: 'white', borderColor: 'rgba(255,255,255,0.6)', fontWeight: 700, px: 2.5, py: 1.5,
                    borderRadius: 2, textTransform: 'none',
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  Ver Cotización (PDF)
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={() => {
                    const pending = filteredQuotations.find(q => q.status === 'PENDING' || q.status === 'DRAFT');
                    if (pending) openRejectDialog(pending);
                  }}
                  sx={{
                    color: 'white', borderColor: 'rgba(255,255,255,0.6)', fontWeight: 700, px: 2.5, py: 1.5,
                    borderRadius: 2, textTransform: 'none',
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  Rechazar
                </Button>
                <Button
                  variant="contained"
                  endIcon={<ArrowForward />}
                  onClick={() => {
                    const pending = filteredQuotations.find(q => q.status === 'PENDING' || q.status === 'DRAFT');
                    if (pending) handleStatusChange(pending.id, 'ACCEPTED');
                  }}
                  sx={{
                    bgcolor: 'white', color: '#2e7d32', fontWeight: 800, px: 3, py: 1.5,
                    borderRadius: 2, textTransform: 'none', fontSize: '1rem',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                    '&:hover': { bgcolor: '#f1f8e9', transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.25)' },
                    transition: 'all 0.2s',
                  }}
                >
                  Aceptar Cotización
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Journey Stepper */}
        <Box sx={{ mb: 4 }}>
          <JourneyStepper />
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Estadísticas */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#667eea' }}>
                    {statistics.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total de Cotizaciones
                  </Typography>
                </Box>
                <Assessment sx={{ fontSize: 48, color: '#667eea', opacity: 0.3 }} />
              </Box>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#f093fb' }}>
                    {statistics.byStatus.PENDING}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pendientes
                  </Typography>
                </Box>
                <Pending sx={{ fontSize: 48, color: '#f093fb', opacity: 0.3 }} />
              </Box>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#43e97b' }}>
                    {statistics.byStatus.ACCEPTED}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Aceptadas
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 48, color: '#43e97b', opacity: 0.3 }} />
              </Box>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#fa709a' }}>
                    {statistics.byStatus.REJECTED}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rechazadas
                  </Typography>
                </Box>
                <Cancel sx={{ fontSize: 48, color: '#fa709a', opacity: 0.3 }} />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Tabla de Cotizaciones */}
        <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Box sx={{ p: 3, bgcolor: '#fafbfc', borderBottom: '1px solid #e1e4e8' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#24292e', mb: 1 }}>
              📊 Registro de Cotizaciones
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Listado completo con filtros avanzados ({filteredQuotations.length} registros)
            </Typography>
          </Box>
          <Box p={3}>
            {/* Filtros */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <FilterList sx={{ color: '#667eea', mr: 1 }} />
            <TextField
              size="small"
              label="Filtrar por Folio"
              value={filters.folio}
              onChange={(e) => handleFilterChange('folio', e.target.value)}
              variant="outlined"
              sx={{ minWidth: 150 }}
            />

            <TextField
              size="small"
              label="Filtrar por Cliente"
              value={filters.cliente}
              onChange={(e) => handleFilterChange('cliente', e.target.value)}
              variant="outlined"
              sx={{ minWidth: 150 }}
            />
            
            <TextField
              size="small"
              label="Filtrar por Empresa"
              value={filters.empresa}
              onChange={(e) => handleFilterChange('empresa', e.target.value)}
              variant="outlined"
              sx={{ minWidth: 150 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Estado</InputLabel>
              <Select
                value={filters.status}
                label="Estado"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="PENDING">Pendiente</MenuItem>
                <MenuItem value="ACCEPTED">Aceptada</MenuItem>
                <MenuItem value="REJECTED">Rechazada</MenuItem>
                <MenuItem value="EXPIRED">Expirada</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Email"
              value={filters.email}
              onChange={(e) => handleFilterChange('email', e.target.value)}
              variant="outlined"
              sx={{ minWidth: 150 }}
            />

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Período</InputLabel>
              <Select
                value={filters.dateRange}
                label="Período"
                onChange={(e) => handleFilterChange('dateRange', e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="week">Última semana</MenuItem>
                <MenuItem value="month">Último mes</MenuItem>
                <MenuItem value="quarter">Último trimestre</MenuItem>
                <MenuItem value="year">Último año</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      {/* Vista de Tabla */}
      {viewMode === 'table' && (
        <Paper elevation={3}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#24292e', fontSize: '0.95rem' }}>Folio</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#24292e', fontSize: '0.95rem' }}>Nombre Comercial</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#24292e', fontSize: '0.95rem' }}>Descripción del Servicio</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#24292e', fontSize: '0.95rem' }}>Importe</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#24292e', fontSize: '0.95rem' }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#24292e', fontSize: '0.95rem' }}>Fecha Creación</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredQuotations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <FolderOpen sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                        No se encontraron cotizaciones
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        No hay cotizaciones disponibles para tu empresa
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredQuotations.map((quotation) => (
                    <TableRow 
                      key={quotation.id} 
                      hover
                      sx={{ 
                        '&:hover': { bgcolor: '#f8f9fa' },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {/* Folio */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#667eea' }}>
                          {quotation.invoiceFolio || quotation.invoice_folio || `COT-${String(quotation.id).padStart(6, '0')}`}
                        </Typography>
                      </TableCell>
                      
                      {/* Nombre Comercial */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ 
                            p: 1, 
                            borderRadius: 2, 
                            bgcolor: '#e3f2fd',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Business sx={{ color: '#1976d2', fontSize: 20 }} />
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#24292e' }}>
                              {quotation.company?.businessName || quotation.businessName || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              RFC: {quotation.company?.rfc || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      
                      {/* Descripción del Servicio */}
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#586069' }}>
                          {quotation.serviceName || 'Servicio no especificado'}
                        </Typography>
                      </TableCell>
                      
                      {/* Importe: subtotal, IVA y total */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {(() => {
                          const { subtotal, iva, totalWithIva } = calculateIva(quotation.subtotal);
                          return (
                            <Box>
                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                Subtotal: {formatMoneyMx(subtotal)}
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                IVA (16%): {formatMoneyMx(iva)}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1rem' }}>
                                Total: {formatMoneyMx(totalWithIva)}
                              </Typography>
                            </Box>
                          );
                        })()}
                      </TableCell>
                      
                      {/* Estado */}
                      <TableCell>
                        {(quotation.status === 'PENDING' || quotation.status === 'DRAFT') ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <Tooltip title="Ver cotización (PDF)">
                              <IconButton size="small" onClick={() => handleViewPdf(quotation)} disabled={pdfLoadingId === quotation.id}>
                                <GetApp fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              onClick={() => openRejectDialog(quotation)}
                              sx={{ borderRadius: 2, textTransform: 'none', whiteSpace: 'nowrap' }}>
                              Rechazar
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              endIcon={<CheckCircleOutline />}
                              onClick={() => handleStatusChange(quotation.id, 'ACCEPTED')}
                              sx={{
                                background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)',
                                color: 'white', fontWeight: 700, borderRadius: 2,
                                textTransform: 'none', whiteSpace: 'nowrap',
                                boxShadow: '0 3px 8px rgba(46,125,50,0.4)',
                                '&:hover': { background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)' },
                              }}>
                              Aceptar
                            </Button>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            {quotation.status === 'ACCEPTED' && <CheckCircle sx={{ fontSize: 18, color: '#43a047' }} />}
                            {quotation.status === 'REJECTED' && <Cancel sx={{ fontSize: 18, color: '#f44336' }} />}
                            {quotation.status === 'EXPIRED'  && <Schedule sx={{ fontSize: 18, color: '#9e9e9e' }} />}
                            {quotation.status === 'CANCELLED' && <Cancel sx={{ fontSize: 18, color: '#757575' }} />}
                            <Typography variant="body2" color="text.secondary">
                              {quotation.status === 'ACCEPTED'  ? 'Aceptada'
                               : quotation.status === 'REJECTED'  ? 'Rechazada'
                               : quotation.status === 'EXPIRED'   ? 'Expirada'
                               : quotation.status === 'CANCELLED' ? 'Cancelada'
                               : quotation.status}
                            </Typography>
                            <Tooltip title="Ver cotización (PDF)">
                              <IconButton size="small" onClick={() => handleViewPdf(quotation)} disabled={pdfLoadingId === quotation.id}>
                                <GetApp fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>

                      {/* Fecha Creación */}
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#586069' }}>
                          {formatDate(quotation.createdAt || quotation.created_at)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Vista de Tarjetas */}
      {viewMode === 'cards' && (
        <Grid container spacing={3}>
          {filteredQuotations.length === 0 ? (
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No se encontraron cotizaciones
                </Typography>
              </Paper>
            </Grid>
          ) : (
            filteredQuotations.map((quotation) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={quotation.id}>
                <Card sx={{
                  height: '100%', borderRadius: 3,
                  boxShadow: (quotation.status === 'PENDING' || quotation.status === 'DRAFT')
                    ? '0 4px 20px rgba(46,125,50,0.25)' : 2,
                  border: (quotation.status === 'PENDING' || quotation.status === 'DRAFT')
                    ? '2px solid #a5d6a7' : '1px solid transparent',
                  transition: 'all 0.2s',
                }}>
                  {(quotation.status === 'PENDING' || quotation.status === 'DRAFT') && (
                    <Box sx={{ bgcolor: '#e8f5e9', px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NotificationsActive sx={{ fontSize: 16, color: '#2e7d32' }} />
                      <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 700 }}>
                        Pendiente de aceptación
                      </Typography>
                    </Box>
                  )}
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Assignment sx={{ color: '#1976d2', fontSize: 24 }} />
                      {getStatusChip(quotation.status)}
                    </Box>

                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                      {quotation.folio || `COT-${String(quotation.id).padStart(6, '0')}`}
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Empresa:</strong> {quotation.businessName || 'N/A'}
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Servicio:</strong> {quotation.serviceName || 'N/A'}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Monto Total</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#2e7d32', lineHeight: 1 }}>
                          ${quotation.totalAmount?.toLocaleString('es-MX', { minimumFractionDigits: 2 }) || '0.00'}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        <CalendarToday sx={{ fontSize: 13, mr: 0.5, verticalAlign: 'middle' }} />
                        {formatDate(quotation.createdAt || quotation.created_at)}
                      </Typography>
                    </Box>
                  </CardContent>

                  <CardActions sx={{ flexDirection: 'column', gap: 1, px: 2, pb: 2 }}>
                    {(quotation.status === 'PENDING' || quotation.status === 'DRAFT') && (
                      <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                        <Button fullWidth variant="outlined" color="error"
                          onClick={() => openRejectDialog(quotation)}
                          sx={{ py: 1.2, borderRadius: 2, textTransform: 'none', fontSize: '0.95rem' }}>
                          Rechazar
                        </Button>
                        <Button fullWidth variant="contained" endIcon={<CheckCircleOutline />}
                          onClick={() => handleStatusChange(quotation.id, 'ACCEPTED')}
                          sx={{
                            background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)',
                            color: 'white', fontWeight: 700, py: 1.2, borderRadius: 2,
                            textTransform: 'none', fontSize: '0.95rem',
                            boxShadow: '0 4px 12px rgba(46,125,50,0.4)',
                            '&:hover': { background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)', transform: 'translateY(-1px)' },
                            transition: 'all 0.2s',
                          }}>
                          Aceptar
                        </Button>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <Button size="small" startIcon={<Visibility />}
                        onClick={() => handleViewDetails(quotation)}
                        sx={{ textTransform: 'none' }}>
                        Ver Detalles
                      </Button>
                      <Button size="small" startIcon={<GetApp />}
                        onClick={() => handleViewPdf(quotation)}
                        disabled={pdfLoadingId === quotation.id}
                        sx={{ textTransform: 'none' }}>
                        {pdfLoadingId === quotation.id ? 'Abriendo...' : 'Ver PDF'}
                      </Button>
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* Dialog de Detalles */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assignment />
            Detalles de la Cotización
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {selectedQuotation && (
            <Grid container spacing={3}>
              {/* Información General */}
              <Grid size={{ xs: 12 }}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1976d2' }}>
                    <Assignment sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Información General
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2"><strong>Folio:</strong> {selectedQuotation.folio || `COT-${String(selectedQuotation.id).padStart(6, '0')}`}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2"><strong>Estado:</strong></Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {getStatusChip(selectedQuotation.status)}
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2"><strong>Descripción del Servicio:</strong> {selectedQuotation.serviceName || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                        <strong>Total con IVA:</strong> {formatMoneyMx(calculateIva(selectedQuotation.totalAmount).totalWithIva)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Inmuebles y conceptos cotizados (todas las entidades, no solo la matriz) */}
              <Grid size={{ xs: 12 }}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1976d2' }}>
                    <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Inmuebles y servicios cotizados
                  </Typography>
                  {detailLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
                  ) : !quotationDetail ? (
                    <Alert severity="warning">No se pudo cargar el desglose. Puedes consultarlo en el PDF.</Alert>
                  ) : (
                    <>
                      {(quotationDetail.entities || []).length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                          {quotationDetail.entities.map((e) => (
                            <Chip key={`${e.entityType}-${e.branchId ?? 'm'}`} size="small" variant="outlined"
                              label={`${e.entityType === 'MATRIZ' ? 'Matriz' : 'Sucursal'}: ${e.entityName}`} />
                          ))}
                        </Box>
                      )}
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700 }}>Concepto</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Inmueble</TableCell>
                              <TableCell sx={{ fontWeight: 700 }} align="right">Importe</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(quotationDetail.additionalServices || []).map((line) => (
                              <TableRow key={line.id}>
                                <TableCell>{line.serviceDescription}</TableCell>
                                <TableCell>{lineEntityName(line, quotationDetail.entities)}</TableCell>
                                <TableCell align="right">{formatMoneyMx(line.unitPrice)}</TableCell>
                              </TableRow>
                            ))}
                            {(() => {
                              const { subtotal, iva, totalWithIva } = calculateIva(quotationDetail.totalAmount);
                              return (
                                <>
                                  <TableRow>
                                    <TableCell colSpan={2} align="right" sx={{ fontWeight: 600 }}>Subtotal</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoneyMx(subtotal)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell colSpan={2} align="right">IVA (16%)</TableCell>
                                    <TableCell align="right">{formatMoneyMx(iva)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell colSpan={2} align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: '#2e7d32' }}>{formatMoneyMx(totalWithIva)}</TableCell>
                                  </TableRow>
                                </>
                              );
                            })()}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </Paper>
              </Grid>

              {/* Información del Cliente */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1976d2' }}>
                    <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Información del Cliente
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Nombre:</strong> {selectedQuotation.clientName || 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Email:</strong> {selectedQuotation.clientEmail || 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Empresa:</strong> {selectedQuotation.businessName || 'N/A'}
                  </Typography>
                </Paper>
              </Grid>

              {/* Información Adicional */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1976d2' }}>
                    <Info sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Información Adicional
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Días Laborales:</strong> {selectedQuotation.workDays || 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Vigencia:</strong> {selectedQuotation.expiresAt ? `hasta el ${formatDate(selectedQuotation.expiresAt)}` : 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Fecha de Creación:</strong> {formatDate(selectedQuotation.createdAt || selectedQuotation.created_at)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          {selectedQuotation && (
            <Button
              startIcon={<GetApp />}
              onClick={() => handleViewPdf(selectedQuotation)}
              disabled={pdfLoadingId === selectedQuotation.id}
            >
              {pdfLoadingId === selectedQuotation.id ? 'Abriendo...' : 'Ver PDF'}
            </Button>
          )}
          <Button onClick={() => setDetailDialogOpen(false)} variant="outlined">
            Cerrar
          </Button>
        </DialogActions>
        </Dialog>

        {/* Dialog de Rechazo — funciona como canal de contacto: en vez de un rechazo
            silencioso, pide motivo/comentario y notifica a un ejecutivo de CONEM */}
        <Dialog
          open={rejectDialogOpen}
          onClose={() => !rejectSubmitting && setRejectDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Rechazar Cotización</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Cuéntanos por qué no vas a continuar con esta cotización. Un ejecutivo de CONEM recibirá tu
              respuesta y se pondrá en contacto contigo para resolver cualquier duda.
            </Typography>

            <FormControl fullWidth error={!!rejectFormError} sx={{ mb: 3 }}>
              <InputLabel id="reject-razon-label">¿Por qué no continúas con el servicio? *</InputLabel>
              <Select
                labelId="reject-razon-label"
                value={rejectForm.razon}
                label="¿Por qué no continúas con el servicio? *"
                onChange={(e) => { setRejectForm(prev => ({ ...prev, razon: e.target.value })); setRejectFormError(''); }}
              >
                <MenuItem value="precio">El precio está fuera de mi presupuesto</MenuItem>
                <MenuItem value="tiempo">No es el momento adecuado</MenuItem>
                <MenuItem value="competencia">Opté por otro proveedor</MenuItem>
                <MenuItem value="info">Necesito más información antes de decidir</MenuItem>
                <MenuItem value="otro">Otro motivo</MenuItem>
              </Select>
              {rejectFormError && <FormHelperText>{rejectFormError}</FormHelperText>}
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Comentarios (opcional)"
              placeholder="Comparte cualquier detalle que nos ayude a resolver tu duda o mejorar la propuesta..."
              value={rejectForm.sugerencia}
              onChange={(e) => setRejectForm(prev => ({ ...prev, sugerencia: e.target.value }))}
              sx={{ mb: 2 }}
            />

            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              ¿Prefieres hablar directamente con nosotros?
              <br />
              ✉ <strong>info@conem.com.mx</strong> &nbsp;·&nbsp; ☎ <strong>229 129 4389</strong>
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setRejectDialogOpen(false)} disabled={rejectSubmitting}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleRejectSubmit}
              disabled={rejectSubmitting}
              startIcon={rejectSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {rejectSubmitting ? 'Enviando...' : 'Enviar y Rechazar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Historial;
